import React, { useState, useCallback } from "react";
import { Autocomplete } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { fetchFiles } from "services/files-category.service";
import debounce from "lodash/debounce";
import { FileEntity } from "types/Files";

const DEBOUNCE_DELAY = 300;

interface FilesSearchProps {
  disabled?: boolean;
  error?: string;
  source?: string;
}

const FilesSearch: React.FC<FilesSearchProps> = ({
  disabled = false,
  source = "profile",
}) => {
  const [searchResults, setSearchResults] = useState<FileEntity[]>([]);
  const [query, setQuery] = useState("");
  const [defaultList, setDefaultList] = useState<FileEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { companyId } = useParams();
  const { t } = useTranslation();

  const handleSearchChange = useCallback(
    debounce(async (value: string) => {
      if (companyId) {
        setLoading(true);
        const results: FileEntity[] | undefined = await searchFiles(
          companyId,
          value,
          source
        );
        setSearchResults(results || []);
        setLoading(false);
      }
    }, DEBOUNCE_DELAY),
    [companyId]
  );

  const onSearchInputChange = useCallback(
    (value: string) => {
      setQuery(value);
      if (value === "") {
        setSearchResults(defaultList);
      } else {
        handleSearchChange(value);
      }
    },
    [handleSearchChange, defaultList]
  );

  const options = searchResults.map((file) => ({
    value: file.id,
    label: `[${file.categories
      ?.map((category) => category.title)
      .join(", ")}] ${file.name || file.title}`,
  }));

  return (
    <>
      <Autocomplete
        options={options}
        selected={[]}
        loading={loading && options.length === 0}
        emptyState={
          !loading && query.length > 0 && options.length === 0 ? (
            <div style={{ padding: "12px 16px", color: "#616161" }}>
              {t("no_documents_found")}
            </div>
          ) : undefined
        }
        onSelect={(selected: any) => {
          if (selected.length) {
            const selectedFile: any = searchResults.find(
              (file) => file.id === selected[0]
            );
            if (selectedFile.fileCategory) {
              navigate(
                source === "document"
                  ? `/app/${companyId}/documents/${selectedFile.fileCategory}/${selectedFile.id}`
                  : `/app/${companyId}/profile/files/${selectedFile.fileCategory}/${selectedFile.id}`
              );
            } else if (
              selectedFile.parentFileId &&
              selectedFile.parentFileCategory
            ) {
              // No category of its own (a child document) — open the file
              // itself, not its parent. `parentFileCategory` only supplies
              // the URL's category segment for navigation; the file page
              // resolves the document by its own id.
              navigate(
                source === "document"
                  ? `/app/${companyId}/documents/${selectedFile.parentFileCategory}/${selectedFile.id}`
                  : `/app/${companyId}/profile/files/${selectedFile.parentFileCategory}/${selectedFile.id}`
              );
            }
          }
        }}
        textField={
          <Autocomplete.TextField
            label={t("search_files")}
            value={query}
            autoComplete="off"
            onChange={onSearchInputChange}
            disabled={disabled}
          />
        }
      />
    </>
  );
};

const searchFiles = async (
  companyId: string,
  query?: string,
  source?: string
): Promise<FileEntity[] | undefined> => {
  const filter = { textSearch: query };
  try {
    const { filesCollection } = await fetchFiles(companyId, filter, source);
    return filesCollection.map((file: any) => ({
      id: file.id,
      title: file.title,
      name: file.name,
      categories: file.filesToCategories,
      fileCategory: file.filesToCategories.length
        ? file.filesToCategories[0].id
        : null,
      parentFileId: file.relatedTo,
      parentFileCategory: file.parentFileCategories?.length
        ? file.parentFileCategories[0]?.id
        : null,
    }));
  } catch (e) {
    return undefined;
  }
};

export default FilesSearch;
